/**
 * Issue #131 — who may open the correction form, and what to send.
 *
 * WHAT THIS CANNOT DECIDE, and the UI is built around the gap rather than
 * pretending it isn't there: the API's Tier 1 also requires that NO OTHER
 * USER has bookmarked or played at the ground, and the client has no way to
 * know that — `GET /api/grounds` does not return other people's bookmarks,
 * and it must not start to. So this predicate is deliberately the SUBSET the
 * client can evaluate honestly, the server owns the real decision, and a
 * save that the server refuses comes back as a 403 carrying prose written to
 * be shown to the person who pressed the button.
 *
 * The alternative — hiding the button unless we are certain — needs a probe
 * endpoint that reports on other users' activity against a ground. That is a
 * worse trade: a small privacy leak, on every ground, to avoid an occasional
 * explained refusal.
 */

import { isPlaceType } from "./placeType";

/**
 * `Number("")` is 0 and `Number("  ")` is 0, which is a real coordinate in
 * the Gulf of Guinea. Every coordinate read below goes through this, so a
 * cleared field is "absent", never "zero".
 */
function coordinate(raw) {
  const text = String(raw ?? "").trim();
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/**
 * Issue #611 — the longest ground name the API accepts, mirroring
 * `GROUND_NAME_MAX_LENGTH` in `api/src/db/repos/grounds.ts`. The server owns
 * the rule (the duplicate check binds one SQL parameter per word of a name,
 * and D1 refuses a query past 100 of them); this copy exists so the form can
 * stop at the bound instead of letting the save be the first thing that
 * mentions it.
 */
export const GROUND_NAME_MAX = 120;

/** Fields the correction endpoint accepts. Order is the form's order. */
export const EDITABLE_GROUND_FIELDS = ["name", "city", "country", "latitude", "longitude", "place_type"];

/**
 * Issue #166 Q3 (Martin, 2026-08-27) — who is an AUTHORITY over standing
 * venue records: "Admin-Only for now, in future it should be Admin and Club
 * ownership." This predicate is the one place that decision lives on the
 * client, mirroring `checkGroundCorrectionGate`'s Tier 2 on the server.
 * When club ownership exists (#158), it widens HERE — one added disjunct —
 * not at the call sites, which is the shape Martin's decision asked for.
 *
 * Deliberately about the USER alone, not the ground: Tier 2 lifts the
 * creator/verified/published conditions wholesale, and the check that stays
 * (a merged record is retired for everyone) is the ground's, tested by the
 * caller.
 */
export function isVenueAuthority(user) {
  return user?.role === "admin";
}

/**
 * Issue #606 — "PRIVATE" AS A STATE THE SCREEN CAN NAME.
 *
 * `is_global OR is_verified` is what the API publishes on
 * (`visibleGroundsWhere`), so a ground with neither is visible to its
 * creator and to nobody else. That was invisible in the UI, and it cost 27
 * venues: an admin hand-entered them through the admin portal believing he
 * was building the shared directory, and every one landed private. Nothing
 * on any screen said so — the map badged such a ground "Your ground", which
 * is true and is not the same sentence.
 *
 * One predicate, one label, used by every surface that draws a ground row,
 * so the state cannot be described two ways or forgotten on one screen.
 */
export const PRIVATE_GROUND_LABEL = "Private — only you can see this";

export function isPrivateGround(ground) {
  return Boolean(ground) && !ground.is_global && !ground.is_verified;
}

/**
 * May this user publish this ground from the admin console (#606)?
 *
 * The client subset of the API's rule, in the shape this module's header
 * describes: the server re-decides on `POST /api/admin/grounds/:id/publish`
 * (`requireRole("admin")` at the router, plus the visibility check that
 * keeps another user's private pin unpublishable), and a refusal comes back
 * as prose written for the person who pressed the button.
 *
 * Everything here is a state the client can read honestly off the row:
 *
 *   - ADMIN ONLY, through `isVenueAuthority` — the same predicate the edit
 *     affordance rides, so publication widens with venue authority (#158's
 *     club owners) or not at all.
 *   - ALREADY PUBLISHED is not an error, but it is not an action either.
 *   - MERGED and ARCHIVED are refused by the API (409 either way), so the
 *     button would be a button that cannot succeed.
 *
 * A ground the console is showing is one this user can already see, so
 * "somebody else's private pin" cannot reach here from that page.
 */
export function canPublishGround(ground, user) {
  if (!ground || !isVenueAuthority(user)) return false;
  if (!isPrivateGround(ground)) return false;
  return !ground.merged_into && !ground.archived_at;
}

export function canOfferGroundEdit(ground, user) {
  if (!ground || !user) return false;
  // A merged ground is a retired record; corrections go to its survivor —
  // for an admin too (the server answers 409 `ground_merged` regardless of
  // role, so offering the form here would be offering a form that cannot
  // save).
  if (ground.merged_into) return false;
  // Tier 2 (#131 / #166 Q4): an admin corrects any standing record — the
  // API has accepted this since #131, and Q4 decided the affordance must
  // exist too. The server re-decides (`checkGroundCorrectionGate` lifts the
  // three creator conditions for admins), so this stays the same honest
  // client subset the creator branch below has always been.
  if (isVenueAuthority(user)) return true;
  // Verified grounds, and grounds somebody else created, are admin work.
  // `is_global` is the other half of the same condition — a ground in the
  // shared directory is in front of other people whether or not anybody has
  // verified it, and the API refuses both. Unreachable until #130 ships
  // publication; offered here so the button does not appear on the day it
  // becomes reachable and 403 every time it is pressed.
  //
  // #166 Q3's conservative half, deliberately unchanged: a CREATOR still
  // loses the affordance the moment their ground is published or verified.
  // "Admin-only for now" governs published grounds until club ownership
  // exists — widening the creator branch would be the loosening Martin's
  // decision declined.
  if (ground.is_verified || ground.is_global) return false;
  // #677: the server no longer hands the client anyone's user id — it sends a
  // server-computed `is_mine` instead, so this can no longer compare ids.
  //
  // WHY THIS BROKE MAIN AND THE UNIT TESTS DID NOT NOTICE. When #677 removed
  // `created_by` from the response, `Boolean(ground.created_by)` became
  // permanently false, so NOBODY was the creator: ground correction, condition
  // updates and facility add/edit all silently lost their affordance. Every
  // frontend test here still passed, because they are fed hand-built ground
  // fixtures that carry `created_by` — a shape the API had stopped sending.
  // Only journeys 16, 16b, 16d and 19 caught it, against a real server.
  //
  // The lesson for the next person: a fixture is a claim about the API, and a
  // test suite of fixtures cannot falsify that claim. `is_mine` is the whole
  // ownership answer now — do not reintroduce an id comparison here.
  return ground.is_mine === true;
}

/**
 * The minimal patch: only what the user actually changed. Sending all five
 * fields every time would stamp an audit trail claiming an edit to fields
 * nobody touched, and `updated_by` is only worth reading if it means
 * something.
 */
export function groundEditDiff(ground, form) {
  const patch = {};
  for (const field of EDITABLE_GROUND_FIELDS) {
    const isNumber = field === "latitude" || field === "longitude";
    const next = isNumber ? coordinate(form[field]) : String(form[field] ?? "").trim();
    if (isNumber && next === null) continue;
    // Issue #136. An unanswered type is dropped from the patch rather than
    // sent as "": the endpoint's enum would 400 on it, and "I did not touch
    // this field" is what an untouched selector means.
    if (field === "place_type" && !isPlaceType(next)) continue;
    const current = isNumber ? coordinate(ground[field]) : String(ground[field] ?? "").trim();
    if (next !== current) patch[field] = next;
  }
  return patch;
}

/** Form state seeded from a ground, as strings — inputs hold strings. */
export function groundEditForm(ground) {
  return {
    name: String(ground?.name ?? ""),
    city: String(ground?.city ?? ""),
    country: String(ground?.country ?? ""),
    latitude: ground?.latitude == null ? "" : String(ground.latitude),
    longitude: ground?.longitude == null ? "" : String(ground.longitude),
    // Issue #136 — seeded from the row, unlike the CREATE flow which starts
    // unanswered. There is no "unanswered" state to preserve here: the
    // ground already has a type, and this form is for changing it.
    place_type: String(ground?.place_type ?? ""),
  };
}

/**
 * Issue #379 — the pin and the two number fields are ONE fact, and this is
 * the conversion between the two ways of stating it.
 *
 * Martin, 2026-08-20: "correcting a pin ... can only be done by manually
 * typing in the Latitude and longitude rather than dragging the pin". Nobody
 * knows a ground's latitude; everybody can see where the pin should be. So
 * the map became a second input onto the same state — the fields did not go
 * away (they are the keyboard-reachable route, and the right one when you
 * have a surveyed coordinate), they stopped being the only one.
 *
 * `via` records WHICH input moved it, and it travels on the pin object
 * rather than beside it — the same reason `fromDevice` does on #134's
 * near-me pin: a flag that describes a coordinate must not be able to
 * desync from the coordinate it describes. The correction form applies (and
 * reverse-geocodes) a `map` pin, and ignores a `field` one, because a `field`
 * pin is the form's own typing coming back to it.
 */
export const GROUND_PIN_FROM_MAP = "map";
export const GROUND_PIN_FROM_FIELD = "field";

/** Six decimals (~11 cm) — what the add flow writes, so the two agree. */
export const GROUND_PIN_PRECISION = 6;

/** A pin only counts if BOTH halves are real numbers; half a coordinate is none. */
export function isGroundPin(pin) {
  return Boolean(pin) && Number.isFinite(pin.lat) && Number.isFinite(pin.lng);
}

/** The pin a ground row is currently on, or `null` for an unmapped ground. */
export function groundPin(ground, via = GROUND_PIN_FROM_FIELD) {
  const lat = coordinate(ground?.latitude);
  const lng = coordinate(ground?.longitude);
  if (lat === null || lng === null) return null;
  return { lat, lng, via };
}

/** The pin the form's two fields currently describe, or `null`. */
export function pinFromForm(form) {
  const lat = coordinate(form?.latitude);
  const lng = coordinate(form?.longitude);
  if (lat === null || lng === null) return null;
  return { lat, lng };
}

/**
 * Same spot? Compared on the numbers, never on object identity — a drag
 * emits a fresh object every time, and typing the same digits back into the
 * field must not read as a move. Tolerance is half of the last kept decimal,
 * so a difference this app cannot even store is not a difference.
 */
export function samePin(a, b) {
  if (!isGroundPin(a) || !isGroundPin(b)) return false;
  const epsilon = 0.5 * 10 ** -GROUND_PIN_PRECISION;
  return Math.abs(a.lat - b.lat) < epsilon && Math.abs(a.lng - b.lng) < epsilon;
}

/**
 * The form, with the pin written into its coordinate fields.
 *
 * Fixed to 6 decimals rather than `String(lat)`: a leaflet drag hands back a
 * float with 15 significant digits, and a form field that reads
 * "-42.45012999999998" after a nudge looks broken, whatever it means.
 */
export function formWithPin(form, pin) {
  if (!isGroundPin(pin)) return form;
  return {
    ...form,
    latitude: pin.lat.toFixed(GROUND_PIN_PRECISION),
    longitude: pin.lng.toFixed(GROUND_PIN_PRECISION),
  };
}

/**
 * Client-side validation mirroring the API's zod schema, so the ordinary
 * typo is answered instantly instead of by a round trip. The server still
 * rejects the same things; this is not the boundary.
 */
export function groundEditErrors(form) {
  const errors = {};
  for (const field of ["name", "city", "country"]) {
    if (!String(form[field] ?? "").trim()) errors[field] = "Required";
  }
  // Issue #611 — the API's bound, said here so the person typing hears it
  // from the form rather than from a 400. Same wording as the facility
  // form's, which has carried the same 120 since #137.
  const name = String(form.name ?? "").trim();
  if (name && name.length > GROUND_NAME_MAX) {
    errors.name = `Use ${GROUND_NAME_MAX} characters or fewer`;
  }
  const lat = coordinate(form.latitude);
  if (lat === null || lat < -90 || lat > 90) errors.latitude = "Must be between -90 and 90";
  const lng = coordinate(form.longitude);
  if (lng === null || lng < -180 || lng > 180) errors.longitude = "Must be between -180 and 180";
  // Issue #136: the closed set, mirrored. A form seeded from a real row is
  // always valid here; this catches a hand-edited value, which is what the
  // API's `z.enum` catches on the other side.
  if (!isPlaceType(String(form.place_type ?? "").trim())) errors.place_type = "Choose a place type";
  return errors;
}
